import React, { useState, useEffect } from "react";
import { func, string, object } from "prop-types";
import { connect } from "react-redux";
import { parseUrl } from "query-string";
import get from "lodash/get";

import PasswordField from "../../atoms/PasswordField";
import Button from "../../atoms/Button";
import MessageView from "../../atoms/MessageView";
import PopupOverlay from "../../molecules/PopupOverlay";

import { resetPassword } from "../../helper/api";
import { validatePassword } from "../../utils/utils";
import { setLoading } from "../../helper/actions";

import "./reset-password.m.css";

const ResetPasswordView = ({ currentPath, setLoading, authHost, sketchesHost, publisherAttributes }) => {
  const [user, setUser] = useState({
    password: "",
    repeatPassword: "",
  });
  const [error, setError] = useState(null);
  const [token, setToken] = useState(null); // Store the token which is received in the query params
  const [passwordMatch, setPasswordMatch] = useState(null);
  const [exceptionPopup, setExceptionPopup] = useState(false);
  const [loginRedirect, setLoginRedirect] = useState(sketchesHost);

  useEffect(() => {
    // To be called only once during the first load
    const params = parseUrl(currentPath);
    setLoginRedirect(global.location.origin);

    if (params) {
      // Validate the opt and member id
      const queryToken = get(params, ["query", "token"], "");

      if (queryToken) {
        // Set the state if both exists
        setToken(queryToken);
      } else {
        setError("Invalid Request !! Please try again");
      }
    } else {
      setError("Invalid Request !! Please try again");
    }
  }, []);

  const setData = (e) => {
    const userObj = { ...user };
    const fieldName = e.target.name;
    userObj[fieldName] = e.target.value;

    // If error has been set then remove it
    if (e.target.value && userObj[`${fieldName}Error`]) {
      userObj[`${fieldName}Error`] = "";
    }

    // If error is set remove it since data is changed
    if (error) {
      setError(null);
    }

    setUser(userObj);
  };

  const onSubmitClick = (e) => {
    e.preventDefault();

    if (validate()) {
      // true -> field errors, false - no fields errors
      return;
    }

    // If error is set remove before making api call
    if (error) {
      setError(null);
    }

    setLoading(true);

    resetPassword(token, user.password, user.repeatPassword)
      .then(() => {
        window.location.href = `${authHost}/sign-in-page?callback-url=${loginRedirect}&redirect-url=${loginRedirect}`;
      })
      .catch((ex) => setExceptionPopup(true))
      .finally(() => setLoading(false));
  };

  const validate = () => {
    const updatedUser = { ...user };
    let flag = false;

    let pwdError = validatePassword(updatedUser.password);

    if (pwdError) {
      updatedUser.passwordError = pwdError;
      flag = true;
    } else {
      updatedUser.passwordError = null;
    }

    pwdError = validatePassword(updatedUser.repeatPassword);

    if (pwdError) {
      updatedUser.repeatPasswordError = pwdError;
      flag = true;
    } else {
      updatedUser.repeatPasswordError = null;
    }

    if (updatedUser.password && updatedUser.repeatPassword) {
      // If password's exists then check if both are same
      if (updatedUser.password !== updatedUser.repeatPassword) {
        setPasswordMatch("Your passwords do not match");
        flag = true;
      } else {
        setPasswordMatch(null);
      }
    }

    // If flag is true, there are field error's
    if (flag) {
      setUser(updatedUser); // update state with field errors
    }

    return flag;
  };

  const commonErrors = () => (
    <>
      {error && (
        <li>
          <MessageView type="error" text={error} />
        </li>
      )}
      {passwordMatch && (
        <li>
          <MessageView type="error" text={passwordMatch} />
        </li>
      )}
      {user.passwordError && (
        <li>
          <MessageView type="error" text={user.passwordError} />
        </li>
      )}
    </>
  );

  const errorView = () => {
    if (user.passwordError === user.repeatPasswordError) {
      return <ul styleName="error-list">{commonErrors()}</ul>;
    }

    return (
      <ul styleName="error-list">
        {commonErrors()}
        {user.repeatPasswordError && (
          <li>
            <MessageView type="error" text={user.repeatPasswordError} />
          </li>
        )}
      </ul>
    );
  };

  const mainView = (
    <React.Fragment>
      <PasswordField value={user.password} placeholder="New Password" name="password" onChange={setData} />
      <PasswordField
        value={user.repeatPassword}
        placeholder="Confirm Password"
        name="repeatPassword"
        onChange={setData}
      />
      <span styleName="message">Use atleast 8 charcaters, mix of numbers & letters</span>

      <span onClick={onSubmitClick}>
        <Button content="RESET PASSWORD" buttonType="type3" />
      </span>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <div styleName={`wrapper ${publisherAttributes.lang}-wrapper`}>
        <div styleName="reset-msg">Reset Password</div>
        <div>
          <div>
            <div styleName="form-wrapper">
              <div styleName="password-field-container">
                {token ? mainView : null}
                {errorView()}
              </div>
            </div>
            {exceptionPopup && (
              <PopupOverlay
                lang={publisherAttributes.lang}
                buttonType="login"
                popupMessage="The password reset link has expired or has already been used"
              />
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

ResetPasswordView.propTypes = {
  currentPath: string,
  setLoading: func,
  authHost: string,
  sketchesHost: string,
  publisherAttributes: object,
};

const mapStateToProps = (state) => ({
  authHost: get(state, ["qt", "config", "publisher-attributes", "auth_host"], null),
  sketchesHost: get(state, ["qt", "config", "sketches-host"], "https://www.prothomalo.com"),
  publisherAttributes: get(state, ["qt", "config", "publisher-attributes"], {}),
  currentPath: get(state, ["qt", "currentPath"], ""),
});

const mapDispatchToProps = (dispatch) => ({
  setLoading: (isLoading) => dispatch(setLoading(isLoading)),
});

const ResetPasswordPage = connect(mapStateToProps, mapDispatchToProps)(ResetPasswordView);

export { ResetPasswordPage };
